/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import { MenuItem } from 'primereact/menuitem';
import { CButtonRounded, CNavLink } from '@/components';
import { AppRoutes } from '@/constants/routes.ts';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

export const useBreadCrumbs = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const backLink: MenuItem | undefined = useMemo(() => {
    if (location.key !== 'default') {
      return {
        template: () => {
          return (
            <CButtonRounded
              outlined
              rounded
              style={{ height: '25px', width: '25px' }}
              severity="secondary"
              onClick={() => history.back()}
              icon="pi pi-arrow-left "
            />
          );
        }
      };
    }
  }, [location]);

  const homeLink: MenuItem = {
    icon: 'pi pi-home',
    template: () => (
      <CNavLink className="p-0 hover:underline" to={AppRoutes.projects.index.url()}>
        <i style={{ fontSize: '16px' }} className="pi pi-shop" />
      </CNavLink>
    )
  };

  const jobListLink: MenuItem = {
    template: () => (
      <CNavLink className="p-0 text-md hover:underline" to={AppRoutes.jobs.list.path}>
        {t(['routes.my_jobs', 'Jobs'])}
      </CNavLink>
    )
  };

  const processListLink: MenuItem = {
    template: () => (
      <CNavLink className="text-md hover:underline" to={AppRoutes.processes.list.path}>
        {t(['routes.published_processes', 'Public Processes'])}
      </CNavLink>
    )
  };

  const projectListLink: MenuItem = {
    template: () => (
      <CNavLink className="text-md hover:underline" to={AppRoutes.projects.index.url()}>
        {t(['routes.projects', 'projects'])}
      </CNavLink>
    )
  };

  const spacesListLink: MenuItem = {
    template: () => (
      <CNavLink className="text-md hover:underline" to={AppRoutes.spaces.index.url()}>
        {t(['routes.spaces', 'Spaces'])}
      </CNavLink>
    )
  };

  return {
    backLink,
    homeLink,
    jobListLink,
    processListLink,
    projectListLink,
    spacesListLink
  };
};

export const useProjectBreadCrumbLink = (Props?: { path: string; link: string }) => {
  const [meta, setMeta] = useState<typeof Props | undefined>(Props);

  const reset = useCallback(() => {
    setMeta(undefined);
  }, []);

  const projectLink: MenuItem | undefined = useMemo(() => {
    if (meta && meta.path) {
      return {
        template: () => (
          <CNavLink className="text-md hover:underline" to={'#'}>
            {meta.path}
          </CNavLink>
        )
      };
    }
  }, [meta]);

  return {
    projectLink,
    setMeta,
    meta,
    reset
  };
};
